import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'next-i18next/pages';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

import { SetInterviewDatesDocument } from '@pflegia/data-access/mutations.generated';
import { SuggestionsTrigger, SegmentTrackingEventName } from '@pflegia/types/common/enums';
import { useToast } from '@pflegia/ui/contexts/toast.context';
import { CHAT_BY_JOB_POSITION_ID } from '@pflegia/ui/pflegia/constants/routes';
import { DateSuggestionProps } from '@pflegia/ui/pflegia/types/date-suggestion-props';
import { useMatchDialogueStore } from '@pflegia/ui/stores/match-dialogue.store';
import { assertIsDefined } from '@pflegia/util/functions/type-guards';

import { useCollectInfoModal } from '@domains/job-board/components/collect-info-modal/useCollectInfoModal';
import { useWhatsAppOptInContext } from '@domains/whatsapp-opt-in/WhatsAppOptInContext';
import { useUpdateWhatsAppNotifications } from '@domains/whatsapp-opt-in/use-update-whatsapp-notifications';
import { useWaOptInEligibility } from '@domains/whatsapp-opt-in/use-wa-opt-in-eligibility';
import { markWaOptInShown } from '@domains/whatsapp-opt-in/wa-opt-in-throttle';

import { useCheckIfSuperFastTrackNeeded } from '@hooks/use-check-if-super-fast-track-needed';

import { trackEvent } from '@shared/application/tracking.service';

import { useSuggestionModalLogic } from './use-suggestion-modal-logic';

/**
 * Custom hook to manage the logic of the match modal.
 *
 * @param withJobIdAssertion checks whether the store is initiated with the job position ID. Set this to false to skip the assertion.
 * This is useful for cases where the job position ID is not immediately available, such as when the logic is redefined per job position in a list.
 * @returns an object containing the modal logic and handlers.
 * @throws {Error} if the job position ID is not defined when `withJobIdAssertion` is true.
 */
export const useMatchModalLogic = (
  { withJobIdAssertion }: { withJobIdAssertion: boolean } = {
    withJobIdAssertion: true,
  },
) => {
  const store = useMatchDialogueStore();
  const { push } = useRouter();
  const { jobPositionId, onClose, title, employerName } = store;
  if (withJobIdAssertion) assertIsDefined(jobPositionId, 'useMatchModalLogic -> Job Position ID');

  const {
    prepareSuggestionsStoreData,
    handleOpen: handleOpenSuggestionsModal,
    handleClose: handleCloseSuggestionsModal,
    setSuggestionsStoreData,
    store: suggestionsStore,
  } = useSuggestionModalLogic();
  const { showAfterModalClose: showSuggestionModalAfterModalClose, trigger: suggestionModalTrigger } = suggestionsStore;

  const checkIfSuperFastTrackNeeded = useCheckIfSuperFastTrackNeeded(jobPositionId);
  const { candidate, showModal: showCollectInfoModal, updateFilters } = useCollectInfoModal();
  const { open: openWhatsAppOptIn } = useWhatsAppOptInContext();
  const { isEligible: isWaOptInEligible } = useWaOptInEligibility();
  const { updateWhatsAppNotifications } = useUpdateWhatsAppNotifications();
  const pendingDatesRef = useRef<Parameters<DateSuggestionProps['onSubmit']>[0] | null>(null);
  const { t } = useTranslation('job');
  const toast = useToast();

  const [setInterviewDatesMutation] = useMutation(SetInterviewDatesDocument, {
    onCompleted: () => {
      if (isWaOptInEligible) {
        markWaOptInShown();
        store.set('state', 'whatsAppOptIn');
      } else {
        store.set('state', 'completed');
      }
      if (store.cameFromSuperFastTrack) {
        prepareSuggestionsStoreData({ jobPositionId, trigger: SuggestionsTrigger.ExitAfterApplication });
      }
      pendingDatesRef.current = null;
    },
    onError: () => {
      toast?.error(t('dateSuggestion.error'));
      pendingDatesRef.current = null;
    },
  });

  useEffect(() => {
    if (store.matchId && pendingDatesRef.current) {
      setInterviewDatesMutation({
        variables: {
          connectionId: store.matchId,
          dates: pendingDatesRef.current,
        },
      });
    }
  }, [store.matchId, setInterviewDatesMutation]);

  const handleDateSuggestionSubmit = useCallback<DateSuggestionProps['onSubmit']>(
    dates => {
      const id = store.matchId;
      if (id) {
        setInterviewDatesMutation({ variables: { connectionId: id, dates } });
      } else {
        pendingDatesRef.current = dates;
      }
    },
    [store.matchId, setInterviewDatesMutation],
  );

  const handleOnCloseCollectInfo = useCallback(() => {
    store.set('state', 'questionIntro');
  }, [store]);

  const acceptOrApply = useCallback(
    (nextStatus: 'pending' | 'accepted') => (matchId: string) => {
      if (showCollectInfoModal) store.set('state', 'collectInfo');
      else handleOnCloseCollectInfo();

      store.set('status', nextStatus);
      store.set('matchId', matchId);
      if (nextStatus === 'accepted') store.set('type', 'application');
      prepareSuggestionsStoreData({ jobPositionId, trigger: SuggestionsTrigger.ExitAfterApplication });
    },
    [handleOnCloseCollectInfo, jobPositionId, prepareSuggestionsStoreData, showCollectInfoModal, store],
  );

  /**
   * Gets called whenever the confirmation button is clicked on the decline modal
   */
  const handleDeclined = useCallback(() => {
    store.set('status', 'feedback');
    if (suggestionModalTrigger !== SuggestionsTrigger.DeclineWithReason) {
      prepareSuggestionsStoreData({ jobPositionId, trigger: SuggestionsTrigger.DeclineWithoutReason });
    }
  }, [jobPositionId, prepareSuggestionsStoreData, store, suggestionModalTrigger]);

  const handleFinishSuperFastTrack = useCallback(() => {
    store.set('state', 'dateSuggestion');
    store.set('cameFromSuperFastTrack', true);
  }, [store]);

  const handleOnClose = useCallback(async () => {
    if (store.state === 'confirmInvitation') {
      trackEvent(SegmentTrackingEventName.InviteModalDismissed, { jobPositionId });
    }
    await updateFilters();

    if (showSuggestionModalAfterModalClose) {
      onClose();
      if (!suggestionsStore.open) {
        handleOpenSuggestionsModal(undefined, true);
      }
    } else {
      handleCloseSuggestionsModal();
    }
    onClose();
  }, [
    handleCloseSuggestionsModal,
    handleOpenSuggestionsModal,
    jobPositionId,
    onClose,
    showSuggestionModalAfterModalClose,
    store.state,
    suggestionsStore.open,
    updateFilters,
  ]);

  const handleWhatsAppOptInDone = useCallback(() => store.set('state', 'completed'), [store]);

  const handleWhatsAppOptInDismissed = useCallback(async () => {
    await updateWhatsAppNotifications(null);
    handleWhatsAppOptInDone();
  }, [handleWhatsAppOptInDone, updateWhatsAppNotifications]);

  const showDeclineExitWhatsAppOptIn = useCallback(() => {
    markWaOptInShown();
    openWhatsAppOptIn('declineExit');
  }, [openWhatsAppOptIn]);

  const handleChecklistClose = useCallback(async () => {
    const isSuperFastTrackNeeded = await checkIfSuperFastTrackNeeded();
    store.set('state', isSuperFastTrackNeeded ? 'superFastTrack' : 'dateSuggestion');
    prepareSuggestionsStoreData({ jobPositionId, trigger: SuggestionsTrigger.ExitAfterApplication });
  }, [checkIfSuperFastTrackNeeded, jobPositionId, prepareSuggestionsStoreData, store]);

  /**
   * Gets called whenever the feedback modal is closed by the Zurück button, the decline
   * submit button, or the dialogue's X while showing decline feedback. When eligible, the
   * WhatsApp prompt is queued to run after the suggestions modal closes.
   */
  const onCloseFeedback = useCallback(() => {
    store.set('status', 'declined');
    setSuggestionsStoreData('afterClose', isWaOptInEligible ? showDeclineExitWhatsAppOptIn : undefined);
    onClose();
    return isWaOptInEligible;
  }, [isWaOptInEligible, onClose, setSuggestionsStoreData, showDeclineExitWhatsAppOptIn, store]);

  /**
   * Gets called whenever the X button or decline button is clicked
   */
  const handleClose = useCallback(() => {
    if (store.state === 'default' && store.status === 'pending') {
      store.set('state', 'dateSuggestion');
      return;
    }
    if (store.state === 'checklist') {
      return handleChecklistClose();
    }
    if (store.state === 'confirmInvitation') {
      trackEvent(SegmentTrackingEventName.InviteModalDismissed, { jobPositionId });
    }
    if (jobPositionId) {
      prepareSuggestionsStoreData({ jobPositionId });
    }
    if (store.state === 'collectInfo') {
      return handleOnCloseCollectInfo();
    }
    if (store.state === 'superFastTrack') {
      return handleFinishSuperFastTrack();
    }
    if (store.state === 'dateSuggestion') {
      if (showSuggestionModalAfterModalClose) {
        handleOpenSuggestionsModal();
      }
      return handleOnClose();
    }
    if (store.state === 'whatsAppOptIn') {
      return handleWhatsAppOptInDismissed();
    }
    if (store.state === 'completed') {
      if (jobPositionId) prepareSuggestionsStoreData({ jobPositionId, initialNavigation: CHAT_BY_JOB_POSITION_ID(jobPositionId) });
      return handleOnClose();
    }
    if (store.state === 'default' && store.status === 'feedback') {
      const shouldShowWhatsAppAfterSuggestions = onCloseFeedback();
      if (showSuggestionModalAfterModalClose) {
        handleOpenSuggestionsModal(undefined, true);
      } else if (shouldShowWhatsAppAfterSuggestions) {
        handleCloseSuggestionsModal();
      }
      return;
    }
    if (showSuggestionModalAfterModalClose) {
      handleOpenSuggestionsModal(undefined, true);
    }
    onClose();
  }, [
    handleChecklistClose,
    handleCloseSuggestionsModal,
    handleFinishSuperFastTrack,
    handleOnClose,
    handleOnCloseCollectInfo,
    handleOpenSuggestionsModal,
    handleWhatsAppOptInDismissed,
    jobPositionId,
    onClose,
    onCloseFeedback,
    prepareSuggestionsStoreData,
    showSuggestionModalAfterModalClose,
    store,
  ]);

  const handleNavigateOnClose = useCallback(
    (nextLink: string) => () => {
      onClose();
      if (showSuggestionModalAfterModalClose) {
        handleOpenSuggestionsModal({ initialNavigation: nextLink }, true);
      } else {
        push(nextLink);
      }
    },
    [handleOpenSuggestionsModal, onClose, push, showSuggestionModalAfterModalClose],
  );

  return {
    // General
    store,
    candidate,
    // Modal Data
    title,
    employerName,
    jobPositionId: jobPositionId as string,
    // Specific
    handleDateSuggestionSubmit,
    showCollectInfoModal,
    onCloseFeedback,
    updateFilters,
    // Handlers
    handleApplied: acceptOrApply('pending'),
    handleAccepted: acceptOrApply('accepted'),
    handleOnCloseCollectInfo,
    handleDeclined,
    handleFinishSuperFastTrack,
    handleWhatsAppOptInDone,
    handleOnClose,
    handleClose,
    handleNavigateOnClose,
  };
};
